import gql from "@/api/gql"

const fetchTemplates = gql`
	query fetchTemplates($where: templateWhereInput, $sort: sortInput) {
		templates(where: $where, sort: $sort) {
			totalCount
			list {
				id
				name
				description
				__typename
				logicalId
				createdAt
				lastModifiedAt
			}
		}
	}
`

const fetchTemplatesByType = gql`
	query fetchTemplatesByType($where: templateWhereInput, $sort: sortInput) {
		templates(where: $where, sort: $sort) {
			list {
				id
				name
				description
				logicalId
				createdAt
				lastModifiedAt
				... on wkhtmltopdfTemplate {
					marginSettings {
						top
					}
				}
				... on emailMjmlTemplate {
					subject
				}
				... on pdfDrawingTemplate {
					drawings {
						page
					}
				}
				... on smsTemplate {
					bodyLiquid
				}

				... on dynamicTemplate {
					dynamicValues {
						valueId
					}
				}
			}
		}
	}
`

const createWkhtmltopdfTemplate = gql`
	mutation createWkhtmltopdfTemplate($input: createWkhtmltopdfTemplateInput!) {
		createWkhtmltopdfTemplate(input: $input) {
			errors
			status
		}
	}
`

const createEmailMjmlTemplate = gql`
	mutation createEmailMjmlTemplate($input: createEmailMjmlTemplateInput!) {
		createEmailMjmlTemplate(input: $input) {
			errors: errors_2 {
				code
				message
			}
			status
		}
	}
`

const createPdfDrawing = gql`
	mutation createPdfDrawing($templateId: String!, $input: addPdfDrawingInput!) {
		addPdfDrawing(templateId: $templateId, input: $input) {
			errors: errors_2 {
				code
				message
			}
			status
		}
	}
`

const createNotificationTemplate = gql`
	mutation createNotificationTemplate($input: createNotificationTemplateInput!) {
		createNotificationTemplate(input: $input) {
			createdStatus {
				id
			}
		}
	}
`

const updateWkhtmltopdfTemplate = gql`
	mutation updateWkhtmltopdfTemplate($templateId: String!, $input: updateWkhtmltopdfTemplateInput!) {
		updateWkhtmltopdfTemplate(templateId: $templateId, input: $input) {
			errors: errors_2 {
				code
				message
			}
			status
		}
	}
`

const createDynamicTemplate = gql`
	mutation createDynamicTemplate($input: createDynamicTemplateInput!) {
		createDynamicTemplate(input: $input) {
			status
			errors
		}
	}
`

const pdfTemplates = gql`
	query pdfTemplates($limit: Int, $skip: Int, $sort: sortInput, $where: templateWhereInput) {
		templates(limit: $limit, skip: $skip, sort: $sort, where: $where) {
			totalCount
			list {
				id
				name
			}
			totalCount
		}
	}
`

const renderWkhtmltopdfTemplate = gql`
	query renderWkhtmltopdfTemplate($templateId: String!, $input: templateRenderParametersInput) {
		renderWkhtmltopdfTemplate(templateId: $templateId, input: $input) {
			errors
			status
			value
		}
	}
`

const renderWkhtmltopdf = gql`
	query renderWkhtmltopdf(
		$html: String!
		$input: templateRenderParametersInput
		$marginSettings: marginSettingsInput
		$headerSettings: headerFooterInput
		$footerSettings: headerFooterInput
	) {
		renderWkhtmltopdf(
			html: $html
			input: $input
			marginSettings: $marginSettings
			headerSettings: $headerSettings
			footerSettings: $footerSettings
		) {
			errors
			status
			value
		}
	}
`

const deleteTemplate = gql`
	mutation deleteTemplate($templateId: String!) {
		deleteTemplate(templateId: $templateId) {
			errors: errors_2 {
				code
				message
			}
			status
		}
	}
`

const fetchWkhtmltopdfTemplateData = gql`
	query fetchWkhtmltopdfTemplateData($where: templateWhereInput, $sort: sortInput) {
		templates(where: $where, sort: $sort) {
			totalCount
			list {
				createdAt
				createdBy {
					id
				}
				id
				description
				name
				logicalId
				lastModifiedAt
				graphQlQuery
				... on wkhtmltopdfTemplate {
					html
					passwordLiquid
					marginSettings {
						top
						right
						bottom
						left
					}
					headerSettings {
						center
						fontName
						fontSize
						html
						left
						line
						right
						spacing
					}
					footerSettings {
						center
						fontName
						fontSize
						html
						left
						line
						right
						spacing
					}
				}
			}
			totalCount
		}
	}
`

const addEmailAttachmentReference = gql`
	mutation addEmailAttachmentReference($templateId: String!, $input: addEmailAttachmentReferenceInput!) {
		addEmailAttachmentReference(templateId: $templateId, input: $input) {
			status
			errors
		}
	}
`

const addEmailAttachmentTemplate = gql`
	mutation addEmailAttachmentTemplate($templateId: String!, $input: addEmailAttachmentTemplateInput!) {
		addEmailAttachmentTemplate(templateId: $templateId, input: $input) {
			status
			errors
		}
	}
`

const removeEmailAttachmentReference = gql`
	mutation removeEmailAttachmentReference($templateId: String!, $attachmentReferenceId: String!) {
		removeEmailAttachmentReference(templateId: $templateId, attachmentReferenceId: $attachmentReferenceId) {
			status
			errors
		}
	}
`

const removeEmailAttachmentTemplate = gql`
	mutation removeEmailAttachmentTemplate($templateId: String!, $attachmentTemplateId: String!) {
		removeEmailAttachmentTemplate(templateId: $templateId, attachmentTemplateId: $attachmentTemplateId) {
			status
			errors
		}
	}
`

const renderEmailAttachments = gql`
	query renderEmailAttachments($templateId: String!, $includeAttachments: Boolean) {
		renderEmailMjmlTemplate(templateId: $templateId, includeAttachments: $includeAttachments) {
			status
			errors
			value {
				attachments {
					bytes
					fileName
				}
			}
		}
	}
`

const renderEmailMjmlTemplate = gql`
	query renderEmailMjmlTemplate($templateId: String!) {
		renderEmailMjmlTemplate(templateId: $templateId) {
			status
			errors
			value {
				html
			}
		}
	}
`

const fetchEmailTemplate = gql`
	query fetchEmailTemplate($where: templateWhereInput, $sort: sortInput) {
		templates(where: $where, sort: $sort) {
			totalCount
			list {
				id
				name
				description
				createdAt
				logicalId
				lastModifiedAt
				... on emailMjmlTemplate {
					graphQlQuery
					mjml
					subject
					attachmentReferences {
						fileName
						filePath
						id
					}
					attachmentTemplates {
						attachmentTemplateId
						fileName
						templateId
					}
				}
			}
		}
	}
`

const updateTemplate = gql`
	mutation updateTemplate($templateId: String!, $input: updateEmailMjmlTemplateInput!) {
		updateEmailMjmlTemplate(templateId: $templateId, input: $input) {
			status
			errors
		}
	}
`

const sendNotification = gql`
	mutation sendNotification($input: sendNotificationInput!) {
		sendNotification(input: $input) {
			errors
			status
		}
	}
`

const fetchNotificationTemplate = gql`
	query templates($where: templateWhereInput) {
		templates(where: $where) {
			list {
				id
				name
				description
				logicalId
				... on notificationTemplate {
					localizedHtml
				}
			}
		}
	}
`

const fetchPdfDrawingTemplate = gql`
	query fetchPdfDrawingTemplate($where: templateWhereInput, $sort: sortInput) {
		templates(where: $where, sort: $sort) {
			totalCount
			list {
				id
				name
				description
				logicalId
				createdAt
				lastModifiedAt
				... on pdfDrawingTemplate {
					graphQlQuery
					fileKey
					password
					passwordLiquid
					drawings {
						id
						font {
							fontStyle
							emSize
							familyName
						}
						liquidSource
						page
						point1 {
							x
							y
						}
						point2 {
							x
							y
						}
						textAlignment
						verticalAlignment
					}
				}
			}
		}
	}
`

const fetchSmsTemplate = gql`
	query templates($where: templateWhereInput) {
		templates(where: $where) {
			list {
				id
				name
				description
				logicalId
				... on smsTemplate {
					bodyLiquid
					graphQlQuery
				}
			}
		}
	}
`

const fetchDynamicTemplate = gql`
	query fetchDynamicTemplate($where: templateWhereInput!) {
		templates(where: $where) {
			list {
				id
				name
				description
				logicalId
				... on dynamicTemplate {
					dynamicValues {
						valueId
						valueJsonString
					}
				}
			}
		}
	}
`

const updatePdfDrawingTemplate = gql`
	mutation updatePdfDrawingTemplate($templateId: String!, $input: createPdfDrawingTemplateInput!) {
		updatePdfDrawingTemplate(templateId: $templateId, input: $input) {
			status
			errors
		}
	}
`

const updateNotificationTemplate = gql`
	mutation updateNotificationTemplate($templateId: String!, $input: updateNotificationTemplateInput!) {
		updateNotificationTemplate(templateId: $templateId, input: $input) {
			status
			errors
		}
	}
`

const updateSmsTemplate = gql`
	mutation updateSmsTemplate($templateId: String!, $input: updateSmsTemplateInput!) {
		updateSmsTemplate(templateId: $templateId, input: $input) {
			errors
			status
		}
	}
`

const updateDynamicTemplate = gql`
	mutation updateDynamicTemplate($templateId: String!, $input: updateDynamicTemplateInput!) {
		updateDynamicTemplate(templateId: $templateId, input: $input) {
			status
			errors
		}
	}
`

const updateDynamicValueOfTemplate = gql`
	mutation updateDynamicValueOfTemplate($templateId: String!, $valueId: String!, $input: updateDynamicValueOfTemplateInput!) {
		updateDynamicValueOfTemplate(templateId: $templateId, valueId: $valueId, input: $input) {
			status
			errors
		}
	}
`

const upsertL10n = gql`
	mutation upsertL10n($l10n: upsertL10nInput) {
		upsertL10n(l10n: $l10n) {
			status
			errors
		}
	}
`

const deleteAllL10n = gql`
	mutation deleteAllL10n($key: String!) {
		deleteAllL10n(key: $key) {
			errors
			status
		}
	}
`

const removeDynamicValue = gql`
	mutation removeDynamicValue($templateId: String!, $valueId: String!) {
		removeDynamicValueFromTemplate(templateId: $templateId, valueId: $valueId) {
			status
			errors
		}
	}
`

const renderHtml = gql`
	query renderHtml($html: String!, $input: templateRenderParametersInput) {
		renderHtml(html: $html, input: $input) {
			errors
			status
			value
		}
	}
`

const updatePdfDrawing = gql`
	mutation updatePdfDrawing($templateId: String!, $drawingId: String!, $input: updatePdfDrawingInput!) {
		updatePdfDrawing(templateId: $templateId, drawingId: $drawingId, input: $input) {
			status
			errors
		}
	}
`

const addPdfDrawing = gql`
	mutation addPdfDrawing($templateId: String!, $input: addPdfDrawingInput!) {
		addPdfDrawing(templateId: $templateId, input: $input) {
			status
			errors
			value
		}
	}
`

const addDynamicValueToTemplate = gql`
	mutation addDynamicValueToTemplate($templateId: String!, $input: addDynamicValueToTemplateInput!) {
		addDynamicValueToTemplate(templateId: $templateId, input: $input) {
			status
			errors
		}
	}
`

const removePdfDrawing = gql`
	mutation removePdfDrawing($templateId: String!, $drawingId: String!) {
		removePdfDrawing(templateId: $templateId, drawingId: $drawingId) {
			status
			errors
		}
	}
`

const renderPdfDrawingTemplate = gql`
	query renderPdfDrawingTemplate($templateId: String!, $input: templateRenderParametersInput!) {
		renderPdfDrawingTemplate(templateId: $templateId, input: $input) {
			status
			errors
			value
		}
	}
`

const renderEmailMjml = gql`
	query renderEmailMjml($emailMjml: String!, $input: templateRenderParametersInput) {
		renderEmailMjml(emailMjml: $emailMjml, input: $input) {
			status
			errors
			value
		}
	}
`

const renderSmsTemplate = gql`
	query renderSmsTemplate($templateId: String!, $input: templateRenderParametersInput!) {
		renderSmsTemplate(templateId: $templateId, input: $input) {
			status
			errors
			value {
				body
			}
		}
	}
`

const renderSms = gql`
	query renderSms($bodyLiquid: String!, $input: templateRenderParametersInput!) {
		renderSms(bodyLiquid: $bodyLiquid, input: $input) {
			status
			errors
			value {
				body
			}
		}
	}
`

const createPdfDrawingTemplate = gql`
	mutation createPdfDrawingTemplate($input: createPdfDrawingTemplateInput!) {
		createPdfDrawingTemplate(input: $input) {
			status
			errors
		}
	}
`

const createSmsTemplate = gql`
	mutation createSmsTemplate($input: createSmsTemplateInput!) {
		createSmsTemplate(input: $input) {
			status
			errors
		}
	}
`

const createFunctionTemplate = gql`
	mutation createFunctionTemplate($input: createFunctionTemplateInput!) {
		createFunctionTemplate(input: $input) {
			status
			errors
		}
	}
`

const fetchFunctionTemplate = gql`
	query fetchFunctionTemplate($where: templateWhereInput!) {
		templates(where: $where) {
			list {
				... on functionTemplate {
					id
					name
					description
					fileKey
					graphQlQuery
					inputs {
						id
						name
						var
						liquid
					}
					outputs {
						id
						name
						var
					}
					engine {
						runTime
						name
					}
				}
			}
		}
	}
`

const updateFunctionTemplate = gql`
	mutation updateFunctionTemplate($id: String!, $input: updateFunctionTemplateInput!) {
		updateFunctionTemplate(id: $id, input: $input) {
			status
			errors
		}
	}
`

const addInputToFunctionTemplate = gql`
	mutation addInputToFunctionTemplate($id: String!, $input: functionInputInput!) {
		addInputToFunctionTemplate(id: $id, input: $input) {
			status
			errors
			createdStatus {
				id
			}
		}
	}
`

const updateInputOfFunctionTemplate = gql`
	mutation updateInputOfFunctionTemplate($templateId: String!, $inputId: String!, $input: functionInputInput!) {
		updateInputOfFunctionTemplate(templateId: $templateId, inputId: $inputId, input: $input) {
			status
			errors
		}
	}
`

const removeInputFromFunctionTemplate = gql`
	mutation removeInputFromFunctionTemplate($templateId: String!, $inputId: String!) {
		removeInputFromFunctionTemplate(templateId: $templateId, inputId: $inputId) {
			status
			errors
		}
	}
`

const addOutputToFunctionTemplate = gql`
	mutation addOutputToFunctionTemplate($id: String!, $input: functionOutputInput!) {
		addOutputToFunctionTemplate(id: $id, input: $input) {
			status
			errors
			createdStatus {
				id
			}
		}
	}
`

const removeOutputFromFunctionTemplate = gql`
	mutation removeOutputFromFunctionTemplate($templateId: String!, $outputId: String!) {
		removeOutputFromFunctionTemplate(templateId: $templateId, outputId: $outputId) {
			status
			errors
		}
	}
`

const renderFunctionTemplate = gql`
	query renderFunctionTemplate($templateId: String!, $input: templateRenderParametersInput!) {
		renderFunctionTemplate(templateId: $templateId, input: $input) {
			status
			errors
			value {
				name
				var
				valueJsonString
			}
		}
	}
`

const query = {
	fetchTemplates,
	fetchTemplatesByType,
	pdfTemplates,
	renderWkhtmltopdfTemplate,
	renderWkhtmltopdf,
	fetchWkhtmltopdfTemplateData,
	renderEmailMjmlTemplate,
	fetchEmailTemplate,
	fetchPdfDrawingTemplate,
	renderEmailAttachments,
	renderPdfDrawingTemplate,
	renderEmailMjml,
	fetchNotificationTemplate,
	renderHtml,
	fetchSmsTemplate,
	renderSmsTemplate,
	fetchDynamicTemplate,
	fetchFunctionTemplate,
	renderSms
}

const mutation = {
	deleteTemplate,
	createWkhtmltopdfTemplate,
	updateWkhtmltopdfTemplate,
	addEmailAttachmentReference,
	addEmailAttachmentTemplate,
	removeEmailAttachmentReference,
	removeEmailAttachmentTemplate,
	updateTemplate,
	sendNotification,
	updateNotificationTemplate,
	deleteAllL10n,
	upsertL10n,
	createEmailMjmlTemplate,
	createPdfDrawing,
	createNotificationTemplate,
	updatePdfDrawingTemplate,
	updatePdfDrawing,
	addPdfDrawing,
	removePdfDrawing,
	createPdfDrawingTemplate,
	createSmsTemplate,
	updateSmsTemplate,
	createDynamicTemplate,
	addDynamicValueToTemplate,
	updateDynamicTemplate,
	updateDynamicValueOfTemplate,
	removeDynamicValue,
	createFunctionTemplate,
	updateFunctionTemplate,
	addInputToFunctionTemplate,
	updateInputOfFunctionTemplate,
	removeInputFromFunctionTemplate,
	addOutputToFunctionTemplate,
	removeOutputFromFunctionTemplate,
	renderFunctionTemplate
}

export { query, mutation }
